// src/Components/ImportMarkers/ImportPhotos.js

import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

export default function ImportPhotos({
  isActive,
  parsedData,
  setParsedData,
  columns,
  setColumns,
  onComplete,
  setAlertState,
  setStagedFiles,
  setIsProcessing,
}) {
  const [step, setStep] = useState('IDLE');
  const fileInputRef = useRef(null);

  // Trigger the flow when the parent component activates this module
  useEffect(() => {
    if (isActive) {
      setStep('ASK_PHOTOS');
    } else {
      setStep('IDLE');
    }
  }, [isActive]);

  const handleAskNo = () => {
    setStep('IDLE');
    onComplete();
  };

  const handleAskYes = () => {
    setStep('INSTRUCT_PHOTOS');
  };

  const handleInstructOk = () => {
    setStep('IDLE');
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // If the user opened the picker but clicked cancel
    if (files.length === 0) {
      onComplete();
      return;
    }

    // Generate Object URLs to pin the files in memory, preventing the OS
    // from releasing the file handles when the input is cleared below.
    // This also arms the File Blob recovery protocol in the Firebase upload loop.
    files.forEach((f) => {
      f.preview = URL.createObjectURL(f);
    });

    // Lift all selected files up to the parent component's memory
    setStagedFiles(files);

    // Find if the Excel file already has a Photos/Images column, or default to adding 'PHOTOS'
    let hasPhotosColumn = columns.some(
      (c) => c.toLowerCase().includes('photo') || c.toLowerCase().includes('image'),
    );
    let photoColName = hasPhotosColumn
      ? columns.find(
          (c) => c.toLowerCase().includes('photo') || c.toLowerCase().includes('image'),
        )
      : 'PHOTOS'; // Ensure uppercase to align with standard mapping nomenclature

    // Dynamically insert the column right after 'Title' or 'Name', or at the end
    if (!hasPhotosColumn) {
      setColumns((prev) => {
        const titleIdx = prev.findIndex(
          (c) => c.toLowerCase().trim() === 'title' || c.toLowerCase().trim() === 'name',
        );

        if (titleIdx !== -1) {
          const newCols = [...prev];
          newCols.splice(titleIdx + 1, 0, 'PHOTOS');
          return newCols;
        }

        // Fallback: just append it to the end
        return [...prev, 'PHOTOS'];
      });
    }

    // The Auto-Matcher (AMENDED: Agnostic universal row search)
    const newParsedData = parsedData.map((row) => {
      // Find matching files based on normalized strings against ALL values in the row
      const matches = files.filter((f) => {
        // 1. Strip the file extension
        let baseName = f.name.replace(/\.[^/.]+$/, '');
        // 2. Strip standard suffixes like -1, _2, or (3) at the end of the string
        baseName = baseName.replace(/(?:[-_ ]?\(\d+\)|[-_]\d+)$/, '');
        // 3. Strip all spaces AND commas, and convert to lowercase
        const cleanFileName = baseName.replace(/[\s,]+/g, '').toLowerCase();

        // 4. GUARANTEED MATCHING: Check if the filename matches ANY cell in this row.
        // This solves the issue of the Title column not being explicitly mapped yet.
        return Object.values(row).some((cellValue) => {
          if (cellValue === null || cellValue === undefined) return false;
          const cleanCell = String(cellValue)
            .replace(/[\s,]+/g, '')
            .toLowerCase();
          return cleanCell !== '' && cleanCell === cleanFileName;
        });
      });

      if (matches.length > 0) {
        return {
          ...row,
          [photoColName]:
            matches.length === 1 ? matches[0].name : `${matches.length} media files`,
          photoMatch: 'MATCH',
          matchedFiles: matches, // Store the array of raw File objects in state for final Firebase upload
        };
      } else {
        return {
          ...row,
          [photoColName]: '',
          photoMatch: 'NO_MATCH',
        };
      }
    });

    setParsedData(newParsedData);

    // Reset the input so the exact same files can be selected again if needed
    e.target.value = null;

    // Move to the memory warning step
    setStep('WARNING_MEMORY');
  };

  const handleWarningOk = () => {
    setStep('IDLE');
    onComplete();
  };

  return (
    <Box>
      <input
        type='file'
        multiple
        accept='image/*, video/*, audio/*'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <ConfirmDialog
        open={step === 'ASK_PHOTOS'}
        onClose={handleAskNo}
        onConfirm={handleAskYes}
        message='Are there photos, videos, or audio to be uploaded as well?'
        severity='info'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
      />

      <ConfirmDialog
        open={step === 'INSTRUCT_PHOTOS'}
        onClose={() => setStep('IDLE')}
        onConfirm={handleInstructOk}
        message='Select all the media files you want to upload in one batch'
        severity='info'
        confirmText='OK'
        hideCancel={true}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
      />

      <ConfirmDialog
        open={step === 'WARNING_MEMORY'}
        onClose={handleWarningOk}
        onConfirm={handleWarningOk}
        message='The media files are stored in browser memory. Do not hit Refresh until you have completed the import task.'
        severity='warning'
        confirmText='OK'
        hideCancel={true}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
      />
    </Box>
  );
}
